"use client";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { PhoneLink, EmailLink } from "@/utils/internal/linkHelpers";
import { CONTACT_INFO } from "@/utils/constants/app";
import { submitQuoteRequestAjax } from "@/utils/actions/requests";
import { toast } from "sonner";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { quoteSchema, QuotePayload } from '@/utils/validation/quote';
import { Spinner } from "@/components/ui/shadcn-io/spinner";

type SelectOption = { value: string; label: string };

export function ContactSection({
  serviceOptions = [],
}: {
  serviceOptions?: SelectOption[];
}) {
  // Initialize form with zod validation
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setError,
    getFieldState,
    formState: { errors, isSubmitting },
  } = useForm<QuotePayload>({
    resolver: zodResolver(quoteSchema),
    mode: 'onSubmit',
  });

  // Manual required field safeguard (no toast) to avoid calling the server with clearly empty forms
  const markMissingRequired = (vals: QuotePayload): number => {
    let missing = 0;
    const ensure = <K extends keyof QuotePayload>(key: K, condition: boolean, message: string) => {
      if (condition) {
        missing++;
        const state = getFieldState(key);
        if (!state.error) {
          setError(key, { type: 'manual', message });
        }
      }
    };
    ensure('firstName', !vals.firstName, 'Nome obbligatorio');
    ensure('lastName', !vals.lastName, 'Cognome obbligatorio');
    ensure('email', !vals.email, 'Email non valida');
    ensure('phone', !vals.phone, 'Telefono obbligatorio');
    if (!vals.privacyAccepted) {
      const state = getFieldState('privacyAccepted');
      if (!state.error) {
        setError('privacyAccepted', { type: 'manual', message: 'Devi accettare la privacy' });
      }
      missing++;
    }
    return missing;
  };

  const onValid = async (data: QuotePayload) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null) formData.append(k, String(v));
    });
    try {
      const result = await submitQuoteRequestAjax(formData);
      if (result.success) {
        toast.success("Richiesta inviata", {
          description: "La tua richiesta di servizio è stata inviata con successo",
        });
        reset();
      } else {
        toast.error("Richiesta non inviata", {
          description: result.error || "Si è verificato un errore",
        });
      }
    } catch (error) {
      console.error('submitQuoteRequestAjax error:', error);
      toast.error("Richiesta non inviata", {
        description: "Si è verificato un errore inatteso",
      });
    }
  };

  // Wrapper to manually gate required-field empties and suppress validation toasts
  const safeSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const vals = getValues();
    const missing = markMissingRequired(vals);
    if (missing > 0) {
      // Do not show toast; red field errors are enough.
      return;
    }
    // Proceed with normal validation (which may still refine), but suppress error toast.
    handleSubmit(onValid, () => { /* no toast on error */ })(e);
  };
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-[1320px] mx-auto px-6 xl:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl mb-4 text-gray-900">
            Contattaci per un Preventivo Gratuito
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            I nostri esperti sono pronti ad aiutarti. Compila il form per
            ricevere un preventivo personalizzato o contattaci direttamente per
            informazioni.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="text-center border-0 shadow-lg">
            <CardContent className="p-6">
              <Phone className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg mb-2 text-gray-900">Telefono</h3>
              <p className="text-gray-600 mb-2">
                Chiamaci per un consulto immediato
              </p>
              <PhoneLink
                phoneNumber={CONTACT_INFO.MAIN_PHONE}
                className="text-green-600 hover:text-green-700 hover:underline"
              />
            </CardContent>
          </Card>

          <Card className="text-center border-0 shadow-lg">
            <CardContent className="p-6">
              <Mail className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg mb-2 text-gray-900">Email</h3>
              <p className="text-gray-600 mb-2">
                Scrivici per informazioni dettagliate
              </p>
              <EmailLink
                email={CONTACT_INFO.MAIN_EMAIL}
                subject="Richiesta informazioni servizi"
                className="text-green-600 hover:text-green-700 hover:underline"
              />
            </CardContent>
          </Card>

          <Card className="text-center border-0 shadow-lg">
            <CardContent className="p-6">
              <Clock className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg mb-2 text-gray-900">Info Orari</h3>
              <p className="text-gray-600 mb-2">Lun-Ven: 9:00-19:00</p>
              <p className="text-gray-600 mb-2">Sab-Dom: Chiuso</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-green-600 text-white">
              <CardTitle className="text-2xl">
                Richiedi Preventivo Gratuito
              </CardTitle>
              <p className="opacity-90">
                Compila il form per ricevere un preventivo personalizzato
              </p>
            </CardHeader>
            {/* Form with client validation */}
            <form onSubmit={safeSubmit}>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      {...register('firstName')}
                      placeholder="Nome *"
                      className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                    />
                    {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName.message}</p>}
                  </div>
                  <div>
                    <Input
                      {...register('lastName')}
                      placeholder="Cognome *"
                      className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                    />
                    {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName.message}</p>}
                  </div>
                </div>
                <div>
                  <Input
                    {...register('email')}
                    placeholder="Email *"
                    type="email"
                    className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                  />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <Input
                    {...register('phone')}
                    placeholder="Telefono *"
                    type="tel"
                    className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                  />
                  {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>}
                </div>
                <Input
                  {...register('address')}
                  placeholder="Indirizzo "
                  className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                />
                <Input
                  {...register('city')}
                  placeholder="Comune "
                  className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                />

                <Select {...register('serviceId')}>
                  <SelectTrigger className="w-full text-sm py-3 border border-gray-300 rounded-md bg-white">
                    <SelectValue placeholder="Servizio richiesto" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-gray-900 border border-gray-300 shadow-lg z-50 max-h-60 overflow-y-auto">
                    {serviceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select {...register('propertyType')}>
                  <SelectTrigger className="w-full text-sm py-3 border border-gray-300 rounded-md bg-white">
                    <SelectValue placeholder="Tipo di immobile" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-gray-900 border border-gray-300 shadow-lg z-50 max-h-60 overflow-y-auto">
                    <SelectItem value="casa">Casa/Appartamento</SelectItem>
                    <SelectItem value="ufficio">Ufficio</SelectItem>
                    <SelectItem value="negozio">Negozio</SelectItem>
                    <SelectItem value="ristorante">Ristorante/Bar</SelectItem>
                    <SelectItem value="magazzino">Magazzino</SelectItem>
                    <SelectItem value="industria">Industria</SelectItem>
                    <SelectItem value="terreno">Terreno</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="struttura ricettiva">
                      Struttura Ricettiva
                    </SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>

                <Textarea
                  {...register('problemDescription')}
                  placeholder="Descrivi il problema (tipo di infestante, zone interessate, quando si è manifestato...)"
                  className="text-lg py-3 border border-gray-300 rounded-md bg-white min-h-32"
                />

                <div>
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="privacy"
                      {...register('privacyAccepted')}
                      className="mt-1"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-600">
                      Accetto il trattamento dei dati personali secondo la Privacy
                      Policy *
                    </label>
                  </div>
                  {errors.privacyAccepted && <p className="text-red-600 text-sm mt-1">{errors.privacyAccepted.message}</p>}
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-transform duration-150 active:scale-[0.97]">
                  {isSubmitting ? (
                    <Spinner className="w-4 h-4 mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? "Invio in corso..." : "Invia Richiesta"}
                </Button>
              </CardContent>
            </form>
          </Card>

          {/* Service Area */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl">
                <MapPin className="w-6 h-6 mr-2 text-green-600" />
                Zone di Intervento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Serviamo Tutta Roma e Provincia
                </h4>
                <p className="text-gray-600 mb-4">
                  I nostri tecnici specializzati operano su tutta la Provincia
                  di Roma, garantendo interventi rapidi e professionali ovunque
                  tu sia.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span>Interventi in tutta Roma e provincia</span>
                </div>

                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span>Preventivo personalizzato in 24h</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span>Sopralluogo gratuito e senza impegno</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span>Intervento entro 48h dalla conferma</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <span>Pronto intervento</span>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-semibold text-gray-900 mb-3">
                  Il Nostro Metodo
                </h5>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>✓ Prodotti eco-compatibili e sicuri</p>
                  <p>✓ Tecniche innovative e testate</p>
                  <p>✓ Personale specializzato e formato</p>
                  <p>✓ Garanzia sui risultati ottenuti</p>
                </div>
              </div>

              <div className="border-l-4 border-green-600 pl-4">
                <h5 className="font-semibold text-gray-900 mb-2">Normativa</h5>
                <p className="text-sm text-gray-600">
                  Siamo aggiornati con le ultime normative europee per la
                  sicurezza in materia di acquisto e utilizzo di prodotti
                  fitosanitari e utilizziamo solo prodotti approvati e
                  certificati dal Ministero della Salute
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
